import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA as MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import firebase from 'firebase/app';
import { FirebaseUtilitiesService, Facility } from '../../services/firebase-utilities.service';
import { UIMessagingService } from '../../services/uimessaging.service';
import { AuthService } from '../../services/auth.service';
import { UserPlans } from '../../dictionaries/UserPlans';
import { UserRoles } from '../../dictionaries/UserRoles';
import { DialogService } from '../../services/dialog.service';
import { InviteFacilityDialogComponent } from './invite-facility-dialog/invite-facility-dialog.component';
import { CreateFacilityFormComponent } from '../create-facility-form/create-facility-form.component';
import { EditFacilityOwnerDialogComponent } from './edit-facility-owner-dialog/edit-facility-owner-dialog.component';
import { environment } from 'src/environments/environment';

interface FacilityWithUsers extends Facility {
  ownerID?: string;
  silentOwnerUid?: string;
  silentOwnerEmail?: string;
  users?: any[];
  loadingUsers?: boolean;
  usersLoaded?: boolean;
}

interface FacilityUser {
  uid?: string;
  name?: string;
  userName?: string;
  email?: string;
  role?: string;
  disabled?: boolean | string;
  pendingInvitation?: { id: string; createdAt: Date } | null;
  revokedInvitation?: { id: string; createdAt: Date } | null;
  invitationOnly?: boolean;
  authMissing?: boolean;
  [key: string]: any;
}

@Component({
  selector: 'app-facilities-list',
  templateUrl: './facilities-list.component.html',
  styleUrls: ['./facilities-list.component.scss'],
  standalone: false
})
export class FacilitiesListComponent implements OnInit {
  facilities: FacilityWithUsers[] = [];
  filteredFacilities: FacilityWithUsers[] = [];
  searchCriteria: string = '';
  loading: boolean = true;
  dbUsers = firebase.firestore().collection('users');

  // Fake data to use if getFacilities fails
  private fakeFacilities: Facility[] = [
    { id: '1', name: 'General Hospital', address: '123 Main St, City, State', patient: '' },
    { id: '2', name: 'City Medical Center', address: '456 Oak Ave, City, State', patient: '' },
    { id: '3', name: 'Regional Health Center', address: '789 Pine Rd, City, State', patient: '' },
    { id: '4', name: 'Community Hospital', address: '321 Elm St, City, State', patient: '' },
    { id: '5', name: 'Memorial Hospital', address: '654 Maple Dr, City, State', patient: '' },
    { id: '6', name: 'University Medical Center', address: '987 College Blvd, City, State', patient: '' },
    { id: '7', name: 'Children\'s Hospital', address: '147 Park Ave, City, State', patient: '' },
    { id: '8', name: 'Regional Medical Center', address: '258 River Rd, City, State', patient: '' }
  ];

  constructor(
    public dialogRef: MatDialogRef<FacilitiesListComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private firebaseUtilities_$: FirebaseUtilitiesService,
    private uiMessaging_$: UIMessagingService,
    private auth_$: AuthService,
    private dialog_$: DialogService,
    private http: HttpClient
  ) {}

  async ngOnInit() {
    await this.loadFacilities();
  }

  async loadFacilities() {
    this.loading = true;
    try {
      const facilities = await this.firebaseUtilities_$.getFacilities();
      if (facilities && facilities.length > 0) {
        const currentFacilityId = this.getCurrentFacilityId();
        // Filter to show only enabled facilities that are not the current user's facility
        const enabledFacilities = facilities.filter((f: any) =>
          (f.disabled === false || f.disabled === 'false' || f.disabled === undefined || f.disabled === null)
          && f.id !== currentFacilityId
        );
        this.facilities = enabledFacilities.map(f => ({ ...f, users: [], loadingUsers: false, usersLoaded: false }));
        this.filteredFacilities = this.facilities;
      } else {
        // Use fake data if no facilities found
        console.log('No facilities found, using fake data');
        this.facilities = this.fakeFacilities.map(f => ({ ...f, users: [], loadingUsers: false, usersLoaded: false }));
        this.filteredFacilities = this.facilities;
      }
    } catch (error) {
      console.error('Error loading facilities:', error);
      // Use fake data on error
      this.facilities = this.fakeFacilities.map(f => ({ ...f, users: [], loadingUsers: false, usersLoaded: false }));
      this.filteredFacilities = this.facilities;
      this.uiMessaging_$.toastMessage('Could not load facilities. Showing sample data.', 'OK');
    } finally {
      this.loading = false;
    }
  }

  async onFacilityExpanded(facility: FacilityWithUsers) {
    if (facility.usersLoaded) {
      return;
    }

    facility.loadingUsers = true;
    try {
      // Load users via Admin SDK Cloud Function — bypasses Firestore allow list rules.
      console.log('[facilities-list] step 1: loading users via CF for', facility.id);
      const cfUsers = await this.loadFacilityUsersViaCF(facility.id);
      console.log('[facilities-list] step 1 done, got', cfUsers.length, 'users');

      // Load pending invitations — isolated catch so a rules deployment gap does not
      // block user loading entirely.
      console.log('[facilities-list] step 2: loading facility_invitations');
      let invitationDocs: firebase.firestore.QueryDocumentSnapshot[] = [];
      try {
        const snap = await firebase.firestore()
          .collection('facility_invitations')
          .where('facilityId', '==', facility.id)
          .where('status', 'in', ['pending', 'revoked'])
          .get();
        invitationDocs = snap.docs;
        console.log('[facilities-list] step 2 done, got', invitationDocs.length, 'invitations');
      } catch (invErr) {
        console.error('[facilities-list] step 2 failed (facility_invitations read):', invErr);
        // Continue without invitation data — users still load correctly.
      }

      // Newest invitation per email wins. Separate into pending and revoked maps.
      const latestByEmail: Record<string, { id: string; createdAt: Date; status: string }> = {};
      invitationDocs.forEach(doc => {
        const d = doc.data();
        const email = (d['email'] || '').toLowerCase();
        const createdAt = d['createdAt']?.toDate ? d['createdAt'].toDate() : new Date(d['createdAt']);
        if (!latestByEmail[email] || createdAt > latestByEmail[email].createdAt) {
          latestByEmail[email] = { id: doc.id, createdAt, status: d['status'] };
        }
      });

      const pendingByEmail: Record<string, { id: string; createdAt: Date }> = {};
      const revokedByEmail: Record<string, { id: string; createdAt: Date }> = {};
      Object.entries(latestByEmail).forEach(([email, inv]) => {
        if (inv.status === 'pending') pendingByEmail[email] = { id: inv.id, createdAt: inv.createdAt };
        else revokedByEmail[email] = { id: inv.id, createdAt: inv.createdAt };
      });

      const realUsers: FacilityUser[] = cfUsers
        .map(user => {
          const email = (user.email || '').toLowerCase();
          user.pendingInvitation = pendingByEmail[email] ?? null;
          return user;
        })
        .filter((user: FacilityUser) => user.disabled !== true && user.disabled !== 'true');

      const realEmails = new Set(realUsers.map(u => (u.email || '').toLowerCase()));

      const orphanPending: FacilityUser[] = Object.entries(pendingByEmail)
        .filter(([email]) => !realEmails.has(email))
        .map(([email, inv]) => ({ email, pendingInvitation: inv, invitationOnly: true }));

      const orphanRevoked: FacilityUser[] = Object.entries(revokedByEmail)
        .filter(([email]) => !realEmails.has(email) && !pendingByEmail[email])
        .map(([email, inv]) => ({ email, revokedInvitation: inv, invitationOnly: true }));

      facility.users = [...realUsers, ...orphanPending, ...orphanRevoked];

      // Mark users who have a Firestore doc but no Firebase Auth account
      console.log('[facilities-list] step 3: checkAuthExists for', realUsers.length, 'users');
      const realUids = realUsers.map(u => u.uid).filter((uid): uid is string => !!uid);
      if (realUids.length > 0) {
        const { notFound } = await this.checkAuthExists(realUids);
        const missingSet = new Set(notFound);
        facility.users.forEach(u => {
          if (u.uid && missingSet.has(u.uid)) u.authMissing = true;
        });
      }
      console.log('[facilities-list] step 3 done');

      facility.usersLoaded = true;
    } catch (error) {
      console.error('[facilities-list] onFacilityExpanded unexpected error:', error);
      facility.users = [];
      this.uiMessaging_$.toastMessage('Could not load users for this facility.', 'OK');
    } finally {
      facility.loadingUsers = false;
    }
  }

  filterFacilities(event: any) {
    const searchTerm = (event?.target?.value || this.searchCriteria || '').toLowerCase().trim();
    
    if (!searchTerm) {
      this.filteredFacilities = this.facilities;
      return;
    }

    this.filteredFacilities = this.facilities.filter(facility => {
      const name = (facility.name || '').toLowerCase();
      const address = (facility.address || '').toLowerCase();
      return name.includes(searchTerm) || address.includes(searchTerm);
    });
  }

  cancelSearch() {
    this.searchCriteria = '';
    this.filteredFacilities = this.facilities;
  }

  getDisplayRole(role: string): string {
    if (!role) return '';
    return role === 'CLIENT_ADMIN' ? 'Facilitator' : role;
  }

  /**
   * Check if the current user has a platinum plan
   */
  hasPlatinumPlan(): boolean {
    const userData = this.auth_$.userData.getValue();
    const plancode = userData?.['plancode'];
    return plancode === UserPlans.platinum;
  }

  /**
   * Get the current user's facility ID
   */
  getCurrentFacilityId(): string {
    const userData = this.auth_$.userData.getValue();
    return userData?.['facility'] || '';
  }

  /**
   * Check if a facility is the current user's facility
   */
  isCurrentFacility(facilityId: string): boolean {
    const currentFacilityId = this.getCurrentFacilityId();
    return facilityId === currentFacilityId;
  }

  /**
   * Check if the INVITE button should be shown for a facility
   * INVITE should NOT show if facility has ownerID (owner already assigned)
   */
  shouldShowInviteButton(facility: FacilityWithUsers): boolean {
    return false; // Never show INVITE button - if ownerID exists, no invite needed
  }

  /**
   * Check if the SEND button should be shown for a facility
   * SEND should only show when ownerID is missing (need to create owner)
   */
  shouldShowSendButton(facility: FacilityWithUsers): boolean {
    return this.hasPlatinumPlan() 
      && !this.isCurrentFacility(facility.id)
      && !facility.ownerID; // Show if ownerID is missing
  }

  /**
   * Handle INVITE button click
   */
  onInviteClick(facility: FacilityWithUsers, event: Event) {
    event.stopPropagation(); // Prevent expansion panel from toggling
    
    const dialogRef = this.dialog_$.open(InviteFacilityDialogComponent, {
      width: '500px',
      data: { facility, mode: 'invite' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.sent) {
        console.log('Invitation sent to:', result.email, 'for facility:', facility.name);
      }
    });
  }

  /**
   * Handle SEND button click (for facilities without ownerID)
   */
  onSendClick(facility: FacilityWithUsers, event: Event) {
    event.stopPropagation();
    const hubFacilityName = this.facilities.find(f => f.id === this.getCurrentFacilityId())?.name || 'NuageDX';
    const dialogRef = this.dialog_$.open(InviteFacilityDialogComponent, {
      width: '500px',
      data: { facility, mode: 'send', hubFacilityName }
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result && result.created) {
        console.log('User created for facility:', facility.name, 'with email:', result.email);
        // Reload facilities to reflect the updated ownerID
        await this.loadFacilities();
      }
    });
  }

  openAddFacility() {
    const dialogRef = this.dialog_$.open(CreateFacilityFormComponent, {
      width: '600px',
      disableClose: false
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        await this.loadFacilities();
      }
    });
  }

  onEditClick(facility: FacilityWithUsers, event: Event) {
    event.stopPropagation();
    const dialogRef = this.dialog_$.open(CreateFacilityFormComponent, {
      width: '600px',
      disableClose: false,
      data: { facility }
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        await this.loadFacilities();
      }
    });
  }

  isOwnerUser(user: FacilityUser): boolean {
    return user.role === UserRoles.owner;
  }

  getCanonicalOwnerLabel(user: FacilityUser, facility: FacilityWithUsers): string | null {
    if (!user.uid) return null;
    if (facility.silentOwnerUid && user.uid === facility.silentOwnerUid) return 'Silent Owner';
    if (facility.ownerID && user.uid === facility.ownerID) return 'Facility Owner';
    return null;
  }

  isCanonicalOwner(user: FacilityUser, facility: FacilityWithUsers): boolean {
    return this.getCanonicalOwnerLabel(user, facility) !== null;
  }

  isDuplicateOwner(user: FacilityUser, facility: FacilityWithUsers): boolean {
    return this.isOwnerUser(user) && !this.isCanonicalOwner(user, facility) && !user.invitationOnly;
  }

  hasNoIdentifiedOwner(facility: FacilityWithUsers): boolean {
    if (facility.usersLoaded) {
      return !(facility.users || []).some(u => this.isOwnerUser(u) && !u.invitationOnly);
    }
    return !facility.ownerID;
  }

  private async loadFacilityUsersViaCF(facilityId: string): Promise<FacilityUser[]> {
    const user = firebase.auth().currentUser;
    if (!user) return [];
    try {
      const idToken = await user.getIdToken();
      const url = `${environment.constants.cloudfunctionsURL}admin-checkAuthUsers`;
      const result = await this.http.post<{ users: FacilityUser[] }>(
        url, { facilityId },
        { headers: new HttpHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` }) }
      ).toPromise();
      return result?.users ?? [];
    } catch (err) {
      console.error('loadFacilityUsersViaCF: CF call failed', err);
      return [];
    }
  }

  private async checkAuthExists(uids: string[]): Promise<{ found: string[]; notFound: string[] }> {
    const user = firebase.auth().currentUser;
    if (!user) return { found: uids, notFound: [] };
    try {
      const idToken = await user.getIdToken();
      const url = `${environment.constants.cloudfunctionsURL}admin-checkAuthUsers`;
      const result = await this.http.post<{ found: string[]; notFound: string[] }>(
        url, { uids },
        { headers: new HttpHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` }) }
      ).toPromise();
      return result ?? { found: uids, notFound: [] };
    } catch (err) {
      console.error('checkAuthExists: CF call failed, assuming all exist', err);
      return { found: uids, notFound: [] };
    }
  }

  isAuthMissingUser(user: FacilityUser): boolean {
    return !!user.authMissing && !user.invitationOnly;
  }

  async onDeleteAuthlessUser(user: FacilityUser, facility: FacilityWithUsers, event: Event): Promise<void> {
    event.stopPropagation();
    if (!user.uid) return;
    try {
      await this.dbUsers.doc(user.uid).delete();
      facility.users = (facility.users || []).filter(u => u !== user);
      this.uiMessaging_$.toastMessage(`${user.name || user.email} removed.`, 'OK');
    } catch (err: any) {
      console.error('Error deleting authless user doc:', err);
      this.uiMessaging_$.toastMessage('Could not remove user. Please try again.', 'OK');
    }
  }

  async onSetAsOwner(user: FacilityUser, facility: FacilityWithUsers, event: Event): Promise<void> {
    event.stopPropagation();
    if (!user.uid) return;
    try {
      await firebase.firestore().collection('facilities').doc(facility.id).update({ ownerID: user.uid });
      facility.ownerID = user.uid;
      this.uiMessaging_$.toastMessage(`${user.name || user.email} is now the facility owner.`, 'OK');
    } catch (err: any) {
      console.error('Error setting facility owner:', err);
      this.uiMessaging_$.toastMessage('Could not update facility owner. Please try again.', 'OK');
    }
  }

  async onRemoveOwner(user: FacilityUser, facility: FacilityWithUsers, event: Event): Promise<void> {
    event.stopPropagation();
    if (!user.uid) return;
    try {
      await this.dbUsers.doc(user.uid).update({ disabled: true });
      facility.users = (facility.users || []).filter(u => u !== user);
      this.uiMessaging_$.toastMessage(`${user.name || user.email} has been removed.`, 'OK');
    } catch (err: any) {
      console.error('Error removing owner:', err);
      this.uiMessaging_$.toastMessage('Could not remove owner. Please try again.', 'OK');
    }
  }

  relativeTime(date: Date): string {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  }

  async onRevokeInvitation(user: FacilityUser, facility: FacilityWithUsers, event: Event): Promise<void> {
    event.stopPropagation();
    if (!user.pendingInvitation) return;
    try {
      await firebase.firestore()
        .collection('facility_invitations')
        .doc(user.pendingInvitation.id)
        .update({
          status: 'revoked',
          revokedAt: firebase.firestore.FieldValue.serverTimestamp()
        });
      if (user.invitationOnly) {
        user.revokedInvitation = { id: user.pendingInvitation.id, createdAt: user.pendingInvitation.createdAt };
        user.pendingInvitation = null;
      } else {
        user.pendingInvitation = null;
      }
      this.uiMessaging_$.toastMessage('Invitation revoked.', 'OK');
    } catch (err: any) {
      console.error('Error revoking invitation:', err);
      this.uiMessaging_$.toastMessage('Could not revoke invitation. Please try again.', 'OK');
    }
  }

  onResendInvitation(user: FacilityUser, facility: FacilityWithUsers, event: Event): void {
    event.stopPropagation();
    const hubFacilityName = this.facilities.find(f => f.id === this.getCurrentFacilityId())?.name || 'NuageDX';
    const dialogRef = this.dialog_$.open(InviteFacilityDialogComponent, {
      width: '500px',
      data: { facility, mode: 'send', hubFacilityName, prefillEmail: user.email || '' }
    });
    dialogRef.afterClosed().subscribe(async result => {
      if (result?.created) {
        facility.usersLoaded = false;
        await this.onFacilityExpanded(facility);
      }
    });
  }

  async onRemoveFromList(user: FacilityUser, facility: FacilityWithUsers, event: Event): Promise<void> {
    event.stopPropagation();
    const invId = user.revokedInvitation?.id || user.pendingInvitation?.id;
    if (invId) {
      try {
        await firebase.firestore().collection('facility_invitations').doc(invId).delete();
      } catch (err) {
        console.error('[facilities-list] Failed to delete invitation doc:', err);
        this.uiMessaging_$.toastMessage('Could not remove user. Please try again.', 'OK');
        return;
      }
    }
    facility.users = (facility.users || []).filter(u => u !== user);
  }

  onAddOwnerClick(facility: FacilityWithUsers, event: Event): void {
    event.stopPropagation();
    const hubFacilityName = this.facilities.find(f => f.id === this.getCurrentFacilityId())?.name || 'NuageDX';
    const dialogRef = this.dialog_$.open(InviteFacilityDialogComponent, {
      width: '500px',
      data: { facility, mode: 'send', hubFacilityName }
    });
    dialogRef.afterClosed().subscribe(async result => {
      if (result?.created) {
        facility.usersLoaded = false;
        await this.onFacilityExpanded(facility);
      }
    });
  }

  onEditUserClick(user: FacilityUser, facility: FacilityWithUsers, event: Event): void {
    event.stopPropagation();
    const hubFacilityId = this.getCurrentFacilityId();
    const hubFacilityName = this.facilities.find(f => f.id === hubFacilityId)?.name || 'NuageDX';
    const dialogRef = this.dialog_$.open(EditFacilityOwnerDialogComponent, {
      width: '480px',
      disableClose: false,
      data: { user, facility, hubFacilityName }
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result?.saved) {
        facility.usersLoaded = false;
        await this.onFacilityExpanded(facility);
      }
    });
  }

  close() {
    this.dialogRef.close();
  }
}
